import { useEffect, useState } from "react";
import { useOutletContext, useParams } from "react-router";
import { Button } from "@/components/Button";
import { DateRangePicker } from "@/components/DateRangePicker";
import { EmptyState } from "@/components/EmptyState";
import { LoadError } from "@/components/LoadError";
import { Pagination } from "@/components/Pagination";
import { SearchField } from "@/components/SearchField";
import { StatusPill } from "@/components/StatusPill";
import { useApi } from "@/lib/api";
import { localDayEnd, localDayStart } from "@/lib/calendarDates";
import { isConfigured } from "@/lib/enums";
import { formatDateTime } from "@/lib/formatDateTime";
import { POLL_INTERVAL_MS } from "@/lib/onlineStatus";
import { PAGE_SIZE, type PagedResult } from "@/lib/pagination";
import { useDebouncedValue } from "@/lib/useDebouncedValue";
import { AiRecordingViewer, UserMark } from "./AiRecordingViewer";
import {
  type AiConversationSummary,
  displayTitle,
  sourceLabel,
} from "./aiShared";
import type { LogsLayoutContext } from "./LogsLayout";
import { SkeletonRows } from "./RecordingsPage";

interface ClaudeStatus {
  status: number;
}

export function AiRecordings() {
  const api = useApi();
  const { orgId } = useParams();
  const { setSummary } = useOutletContext<LogsLayoutContext>();

  const [recordings, setRecordings] = useState<AiConversationSummary[] | null>(
    null,
  );
  const [totalCount, setTotalCount] = useState(0);
  const [claude, setClaude] = useState<ClaudeStatus | null>(null);
  const [page, setPage] = useState(1);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebouncedValue(search, 300);
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [viewing, setViewing] = useState<AiConversationSummary | null>(null);

  useEffect(() => {
    if (!orgId) return;

    let cancelled = false;

    async function load() {
      const params = new URLSearchParams({
        page: String(page),
        pageSize: String(PAGE_SIZE),
      });

      if (debouncedSearch) params.set("search", debouncedSearch);
      const from = localDayStart(dateFrom);
      if (from) params.set("from", from);
      const to = localDayEnd(dateTo);
      if (to) params.set("to", to);

      const [data, status] = await Promise.all([
        api.get<PagedResult<AiConversationSummary>>(
          `/organizations/${orgId}/ai-conversations?${params.toString()}`,
        ),
        api.get<ClaudeStatus>(`/organizations/${orgId}/claude`),
      ]);

      if (cancelled) return;

      if (status) setClaude(status);

      if (data) {
        setRecordings(data.items);
        setTotalCount(data.totalCount);
        setError(null);
      } else {
        setError("Couldn't load AI recordings.");
      }
    }

    void load();

    const interval = setInterval(() => void load(), POLL_INTERVAL_MS);

    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [api, orgId, page, debouncedSearch, dateFrom, dateTo]);

  useEffect(() => {
    if (!recordings) return;

    setSummary({ total: totalCount, noun: "recording" });

    return () => setSummary(null);
  }, [recordings, totalCount, setSummary]);

  const hasFilters = Boolean(debouncedSearch || dateFrom || dateTo);
  const isEmpty = recordings !== null && totalCount === 0;
  const connected = claude !== null && isConfigured(claude.status);

  function clearFilters() {
    setSearch("");
    setDateFrom("");
    setDateTo("");
    setPage(1);
  }

  return (
    <div>
      <div className="mb-4 flex flex-wrap gap-2">
        <SearchField
          value={search}
          onChange={(value) => {
            setSearch(value);
            setPage(1);
          }}
          label="Search AI recordings"
          placeholder="Search title, email or user"
        />
        <DateRangePicker
          from={dateFrom}
          to={dateTo}
          onChange={(newFrom, newTo) => {
            setDateFrom(newFrom);
            setDateTo(newTo);
            setPage(1);
          }}
        />
      </div>

      {error && !recordings && (
        <LoadError
          what="AI recordings"
          onRetry={() => setPage(1)}
          className="mb-4"
        />
      )}

      {isEmpty && claude && !connected && !hasFilters ? (
        <EmptyState
          title="No AI recordings yet"
          description="Recorded conversations will appear here once you connect your AI provider."
        ></EmptyState>
      ) : isEmpty ? (
        <EmptyState
          title={
            hasFilters
              ? "No AI recordings match these filters"
              : "No AI recordings yet"
          }
          description={
            hasFilters
              ? "Try a different search, date range or filter."
              : "Chats appear here a few minutes after people use Claude."
          }
        >
          {hasFilters && <Button onClick={clearFilters}>Clear filters</Button>}
        </EmptyState>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full min-w-[48rem] text-left text-sm">
            <thead>
              <tr>
                {["Recording", "User", "Started", "Messages"].map((column) => (
                  <th
                    key={column}
                    scope="col"
                    className="border-b border-surface pb-2.5 pr-4 text-xs font-semibold uppercase tracking-wider text-foreground/70"
                  >
                    {column}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {!recordings ? (
                <SkeletonRows columns={2} />
              ) : (
                recordings.map((recording) => (
                  <tr
                    key={recording.id}
                    className="h-14 cursor-pointer border-b border-surface/70 hover:bg-surface"
                    onClick={() => setViewing(recording)}
                  >
                    <td className="py-2 pr-4">
                      <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            setViewing(recording);
                          }}
                          className="text-left font-medium text-foreground hover:underline"
                        >
                          {displayTitle(recording)}
                        </button>
                        {recording.deletedAt && (
                          <StatusPill variant="warning">
                            Deleted by user
                          </StatusPill>
                        )}
                      </div>
                      <span className="text-xs text-foreground/70">
                        {[sourceLabel(recording.source), recording.model]
                          .filter(Boolean)
                          .join(" · ")}
                      </span>
                    </td>
                    <td className="py-2 pr-4">
                      <div className="flex items-center gap-2.5">
                        <UserMark recording={recording} />
                        <div className="flex min-w-0 flex-col items-start gap-0.5">
                          <span className="truncate text-foreground/70">
                            {recording.tendraUserName ??
                              recording.email ??
                              "Unknown user"}
                          </span>
                          {!recording.tendraUserId && (
                            <StatusPill variant="idle">
                              Not in Tendra
                            </StatusPill>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="whitespace-nowrap py-2 pr-4 tabular-nums text-foreground/70">
                      {formatDateTime(recording.createdAt)}
                    </td>
                    <td className="py-2 pr-4 tabular-nums">
                      {recording.messageCount}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}

      {!isEmpty && (
        <Pagination
          page={page}
          totalPages={Math.ceil(totalCount / PAGE_SIZE)}
          onPageChange={setPage}
        />
      )}

      {viewing && orgId && (
        <AiRecordingViewer
          orgId={orgId}
          recording={viewing}
          onClose={() => setViewing(null)}
        />
      )}
    </div>
  );
}
